import { Button } from '@innovationdepartment/proxima-ui';
import { Link } from 'react-router';
import DeliverTo, { type DeliverToProps } from '../../components/DeliverTo';
import { DeliverToBody, Notice } from '../../components/form.styles';
import type { UnconfirmedCreate } from '../../store/wizardTypes';

type UnconfirmedCreateProps = {
  unconfirmedCreate: UnconfirmedCreate | null;
  viewerPath: string;
  /** Creates the draft even though the list may already hold it. */
  onCreateAnyway: () => void;
  createAnywayDisabled: boolean;
};

export type DeliverStepProps = Omit<DeliverToProps, 'deliveredAccountIds'> & UnconfirmedCreateProps;

/** The wizard's Deliver step: the Deliver-to form, then what an unconfirmed create found. */
const DeliverStep = ({
  unconfirmedCreate,
  viewerPath,
  onCreateAnyway,
  createAnywayDisabled,
  ...props
}: DeliverStepProps) => (
  <DeliverToBody>
    <DeliverTo {...props} />
    {unconfirmedCreate ? (
      <UnconfirmedCreateNotice
        unconfirmed={unconfirmedCreate}
        viewerPath={viewerPath}
        onCreateAnyway={onCreateAnyway}
        createAnywayDisabled={createAnywayDisabled}
      />
    ) : null}
  </DeliverToBody>
);

/**
 * What the list showed after a create that may or may not have saved, so the user can open a saved audience instead of creating it twice.
 *
 * `found` and `unchecked` keep the draft and offer Create it anyway, since the check cannot prove the match is this create's row.
 */
const UnconfirmedCreateNotice = ({
  unconfirmed,
  viewerPath,
  onCreateAnyway,
  createAnywayDisabled
}: Omit<UnconfirmedCreateProps, 'unconfirmedCreate'> & { unconfirmed: UnconfirmedCreate }) => {
  const createAnyway = (
    <Button
      label="Create it anyway"
      variant="outlined"
      size="small"
      disabled={createAnywayDisabled}
      onClick={onCreateAnyway}
    />
  );
  switch (unconfirmed.kind) {
    case 'found':
      return (
        <Notice tone="warning" role="alert">
          We couldn't confirm the create, but it looks like this audience was saved as{' '}
          <Link to={`${viewerPath}/${unconfirmed.definitionId}`}>{unconfirmed.name}</Link>. Open it,
          or create it anyway if that isn't this audience. {createAnyway}
        </Notice>
      );
    case 'notFound':
      return (
        <Notice tone="error" role="alert">
          We couldn't confirm the audience was saved, and it isn't on the audiences list. Try
          creating it again.
        </Notice>
      );
    default:
      return (
        <Notice tone="error" role="alert">
          We couldn't confirm the audience was saved. Check the{' '}
          <Link to={viewerPath}>audiences list</Link> before creating it again. {createAnyway}
        </Notice>
      );
  }
};

export default DeliverStep;
